import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { dayLabel } from "../lib/time";
import { ClientChip, Sheet } from "./bits";
import { fmtH, hm, pct, plural, flagged, monthName, monthShort, nextMonthStart, az, GROUPS, GROUP_LABEL, GroupLabel, SplitBar, groupOf, TEAM_NAME } from "../lib/hours";

const TYPE = { retainer: "Retainer", project: "Project" };
const KIND = { new: "New", revision: "Revision", adapt: "Adapt", standby: "If any" };

function Figures({ c, period, empty }) {
  const pr = c.minutes ? c.revisionMinutes / c.minutes : 0;
  return (
    <div className="cs-figs">
      <div><div className="lbl">Total hours</div><b className="num f48" data-testid="cs-total">{hm(c.minutes)}</b><p className="hint0">{period}</p>
        {c.minutes > 0 && <><SplitBar c={c} h={10} />
          <p className="num hint0">{[["design", c.designMinutes], ["video", c.videoMinutes], ["other", c.otherMinutes]].filter(([, m]) => m > 0).map(([g, m]) => `${GROUP_LABEL[g]} ${hm(m)}`).join(" / ")}</p></>}</div>
      <div><div className="lbl">Revision hours</div><b className="num f30">{flagged(c) && <span className="warn-i" aria-hidden="true">▲ </span>}{hm(c.revisionMinutes)}</b>
        <p className={`hint0 ${flagged(c) ? "strong" : ""}`}>{c.minutes ? `${pct(pr)} of the hours${flagged(c) ? ", over 30%" : ""}.` : empty}</p></div>
      <div><div className="lbl">People</div><b className="num f30">{c.headcount}</b><p className="hint0">{c.headcount === 1 ? "person logged time" : "people logged time"}</p></div>
    </div>
  );
}

function PersonMonths({ trend, months, pick, people, untracked }) {
  const byId = Object.fromEntries(people.map((p) => [p.id, p]));
  const rows = {};
  trend.forEach((c, i) => (c?.people || []).forEach((p) => {
    const k = `${p.userId}|${p.team}`;
    rows[k] = rows[k] || { id: p.userId, team: p.team, m: [0, 0, 0], r: [0, 0, 0] };
    rows[k].m[i] += p.minutes; rows[k].r[i] += p.revisionMinutes;
  }));
  const list = Object.values(rows);
  if (!list.length) return <p className="hint">No hours on this client in the last 3 months.</p>;
  const name = (id) => byId[id]?.name || id;
  const sum = (a) => a.reduce((s, x) => s + x, 0);
  const head = (m, i) => `${monthShort(m)}${i === 2 ? " (so far)" : ""}`;
  return (
    <div className="pm-wrap" role="region" tabIndex={0} aria-label="Hours per person by month">
      <table className="pm" data-testid="cs-person-months">
        <caption className="sr-only">Hours per person on this client, {monthName(months[0])} to {monthName(months[2])}</caption>
        <thead><tr><th scope="col">Person</th>{months.map((m, i) => <th key={m} scope="col" className={m === pick ? "on" : ""}>{head(m, i)}</th>)}<th scope="col">3 months</th></tr></thead>
        {GROUPS.map((g) => {
          const gl = list.filter((x) => groupOf(x.team) === g).sort((a, b) => sum(b.m) - sum(a.m) || az(name(a.id), name(b.id)));
          if (!gl.length) return null;
          return (
            <tbody key={g}><tr><th scope="rowgroup" colSpan={5}><GroupLabel g={g} /></th></tr>
              {gl.map((x) => (
                <tr key={`${x.id}|${x.team}`}><th scope="row" title={byId[x.id] ? `${byId[x.id].name}, ${byId[x.id].designation}` : ""}>{name(x.id)}{g === "other" ? `, ${TEAM_NAME[x.team] || x.team}` : ""}</th>
                  {x.m.map((v, i) => <td key={i} className={months[i] === pick ? "on" : ""}>{untracked[i] ? "" : <><span className={v ? "" : "zero"}>{hm(v)}</span>{x.r[i] > 0 && <small>{hm(x.r[i])} rev</small>}</>}</td>)}
                  <td><b>{hm(sum(x.m))}</b></td></tr>))}
            </tbody>);
        })}
        <tfoot>
          <tr className="rev"><th scope="row">Revisions</th>{trend.map((c, i) => <td key={i} className={months[i] === pick ? "on" : ""}>{hm(c?.revisionMinutes || 0)}</td>)}<td>{hm(sum(trend.map((c) => c?.revisionMinutes || 0)))}</td></tr>
          <tr className="tot"><th scope="row">Total</th>{trend.map((c, i) => <td key={i} className={months[i] === pick ? "on" : ""}>{hm(c?.minutes || 0)}</td>)}<td>{hm(sum(trend.map((c) => c?.minutes || 0)))}</td></tr>
        </tfoot>
      </table>
    </div>
  );
}

function Bars({ items, testid }) {
  const max = Math.max(1, ...items.map((x) => x.v));
  return <div className="kbars" data-testid={testid}>{items.map((x) => (
    <div key={x.k} className="kbar"><span>{x.k}</span><div className="kb-track"><i className={x.hatch ? "hatch" : ""} style={{ width: `${(x.v / max) * 100}%`, "--c": x.c }} /></div><b className="num">{x.label}</b></div>))}</div>;
}

function WhoWorked({ c, byId }) {
  const list = [...c.people].sort((a, b) => b.minutes - a.minutes);
  const max = Math.max(1, ...list.map((p) => p.minutes));
  if (!list.length) return null;
  return (
    <section className="cs-sec"><div className="cs-sh"><h3 className="lbl">Who worked on it</h3><span className="hint0">{plural(list.length, "person", "people")}</span></div>
      {GROUPS.map((g) => { const gl = list.filter((p) => groupOf(p.team) === g); return gl.length > 0 && (
        <div key={g} className="ww-g" data-team={g === "other" ? undefined : g}><GroupLabel g={g} hours={gl.reduce((s, p) => s + p.minutes, 0) / 60} />
          {gl.map((p) => (
            <div key={`${p.userId}|${p.team}`} className="ww-row" data-testid={`cs-who-${p.userId}`}>
              <span className="trunc" title={byId[p.userId] ? `${byId[p.userId].name}, ${byId[p.userId].designation}` : ""}>{byId[p.userId]?.name || p.userId}{g === "other" ? `, ${TEAM_NAME[p.team] || p.team}` : ""}</span>
              <span className="ww-track"><i style={{ width: `${(p.minutes / max) * 100}%` }} /><i className="ww-rev" style={{ width: `${(p.revisionMinutes / max) * 100}%` }} /></span>
              <b className="num">{hm(p.minutes)}</b><span className="num i2">{p.revisionMinutes > 0 ? `${hm(p.revisionMinutes)} rev` : ""}</span>
            </div>))}
        </div>); })}
      <p className="hint0">The hatched part of each bar is that person's revision hours.</p>
    </section>
  );
}

function RemoveBlock({ c, onRemove }) {
  const [ask, setAsk] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <section className="cs-sec cs-remove" data-testid="remove-client-block">
      {!ask ? <button className="btn-ghost sm danger" onClick={() => setAsk(true)} data-testid="remove-client-button">Remove client</button> : (
        <div className="rm-confirm" role="alertdialog" aria-label={`Remove ${c.name}?`} data-no-shortcuts>
          <b>Remove {c.name}?</b>
          <p className="hint0">It leaves Clients and the client pickers for new cards. Nothing is deleted: its past hours stay in the Excel export and in history. You can restore it from Removed clients.</p>
          {err && <p className="err" role="alert" data-testid="remove-client-error">{err}</p>}
          <div className="cs-actions">
            <button className="btn-solid danger-bg" disabled={busy} onClick={async () => { setBusy(true); const e = await onRemove(); setBusy(false); if (e) setErr(e); }} data-testid="remove-client-confirm">Remove client</button>
            <button className="btn-ghost sm" onClick={() => { setAsk(false); setErr(""); }} data-testid="remove-client-cancel">Keep it</button>
          </div>
        </div>)}
    </section>
  );
}

export function ClientSheet({ c, client, trend, months, pick, running, people, untracked, onEdit, onRemove, onClose }) {
  const [d, setD] = useState(null);
  const [ms, setMs] = useState(null);
  useEffect(() => {
    api(`/clients/${c.code}/detail?month=${pick}`).then((r) => r.ok && setD(r.data));
    api(`/clients/${c.code}/months`).then((r) => r.ok && setMs(r.data));
  }, [c.code, pick, c.minutes]);
  const byId = Object.fromEntries(people.map((p) => [p.id, p]));
  const period = running ? `${monthName(pick)} so far` : monthName(pick);
  const color = `oklch(0.62 0.15 ${client?.hue ?? 280})`;
  const owners = (c.owners || []).map((id) => byId[id]?.name).filter(Boolean);
  const td = ms?.toDate;
  const total = d ? d.byKind.new + d.byKind.revision + d.byKind.adapt + d.byKind.standby : 0;
  return (
    <Sheet title={c.name} onClose={onClose} testid="client-detail" width={680}>
      <div className="cs-top"><ClientChip code={c.code} name={c.name} /><span className="tag tag-outline">{TYPE[c.type]}</span>
        {onEdit && <button className="btn-ghost sm ml-auto" onClick={onEdit} data-testid="edit-client-button">Edit client</button>}</div>
      <p className="hint0" data-testid="cs-serviced-by">{owners.length ? <>Serviced by <b>{owners.join(", ")}</b>.</> : "Serviced by nobody yet."}</p>
      <Figures c={c} period={period} empty={running ? "No hours yet." : `No hours in ${monthName(pick)}.`} />
      {c.type === "project" && td && td.minutes > 0 && <p className="todate" data-testid="cs-todate">To date, from the first hour on {dayLabel(td.firstDay)} up to today: <b>{hm(td.minutes)}</b> in all, <b>{hm(td.revisionMinutes)}</b> of it revisions, from {plural(td.headcount, "person", "people")}.</p>}
      <WhoWorked c={c} byId={byId} />
      <section className="cs-sec"><div className="cs-sh"><h3 className="lbl">Last 3 months</h3><span className="hint0">{monthShort(months[0])} to {monthShort(months[2])}, this month so far</span></div>
        <PersonMonths trend={trend} months={months} pick={pick} people={people} untracked={untracked} />
        <p className="hint0">The shaded column is the month picked on Clients. Small "rev" figures are each person's revision hours, inside their total.</p></section>
      {c.minutes > 0 && d && <>
        <section className="cs-sec"><div className="cs-sh"><h3 className="lbl">Hours by week</h3><span className="hint0">{period}</span></div>
          <Bars testid="cs-weeks" items={d.byWeek.filter((w) => w.minutes > 0).map((w) => ({ k: w.from === w.to ? dayLabel(w.from).slice(4) : `${Number(w.from.slice(8))} to ${dayLabel(w.to).slice(4)}`, v: w.minutes, label: hm(w.minutes), c: color }))} /></section>
        <section className="cs-sec"><div className="cs-sh"><h3 className="lbl">Hours by kind</h3><span className="hint0">{pct(total ? d.byKind.revision / total : 0)} revision hours{flagged(c) ? ", over 30%" : ""}</span></div>
          <Bars testid="cs-kinds" items={[["New work", "new"], ["Revisions", "revision"], ["Adapts", "adapt"], ["If any", "standby"]].filter(([, k]) => k !== "standby" || d.byKind.standby > 0)
            .map(([l, k]) => ({ k: l, v: d.byKind[k], label: `${hm(d.byKind[k])} ${pct(total ? d.byKind[k] / total : 0)}`, c: color, hatch: k === "revision" }))} />
          <p className="hint0">Revision hours above 30% of a client's hours are flagged.</p></section>
      </>}
      <section className="cs-sec"><div className="cs-sh"><h3 className="lbl">Top 8 cards by time</h3></div>
        {d && !d.topCards.length && <p className="hint0">No cards with time in {monthName(pick)}.</p>}
        <ol className="topcards" data-testid="cs-topcards">{(d?.topCards || []).map((t, i) => (
          <li key={t.taskId}><span className="num rank">{i + 1}</span><div><b>{t.title || "Card no longer on a list"}</b>
            <p className="hint0">{[byId[t.assignee]?.short, t.date && dayLabel(t.date), KIND[t.kind], t.unplanned && "Unplanned"].filter(Boolean).join(", ")}</p></div><b className="num">{hm(t.minutes)}</b></li>))}</ol></section>
      <section className="cs-sec"><div className="cs-sh"><h3 className="lbl">How the hours are counted</h3></div>
        <ol className="howto">
          <li>Every timer minute on {c.name} in {monthName(pick)}, Indian time, in whole minutes, the same way the Excel export counts them.{running ? " A timer that is still running counts up to now." : ""}</li>
          <li>{running ? `The month starts again from 0 on ${nextMonthStart(pick)}. ${monthName(pick)} then closes with its final hours.` : `${monthName(pick)} is closed, so these hours are final.`} Clients keeps this month and the two before it; the Excel export covers any other period.</li>
          <li>Revisions are the hours on cards that came back as changes (feedback rounds). They are part of the total, not extra.</li>
          <li>Design and Video are the team each person was in when their timer started, so a later team move does not re-file old hours.</li>
        </ol></section>
      {onRemove && <RemoveBlock c={c} onRemove={onRemove} />}
    </Sheet>
  );
}
